/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import { Injectable } from '@angular/core';

import {
    ILogListExpandedContentState,
    LogListExpandedContentStore,
} from '../../log-list-expanded-content/log-list-expanded-content.store';

import { TTrafficLog } from '../traffic-logs.types';
import { ILogsStates, LogsStore } from '../../../services/logs.store';

import { isObjectEmpty } from '../../../utils/logs.utils';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ITrafficLogListExpandedContentState extends Omit<ILogListExpandedContentState, 'eventLog'> {}

const initialState: ITrafficLogListExpandedContentState = {
    applicationLog: {},
    connectionLog: {},
};

/**
 * @description
 *     State and Effect management for the traffic log expandeded content component.
 * @author Zhiqian Liu
 */
@Injectable()
export class TrafficLogListExpandedContentStore extends
    LogListExpandedContentStore<ITrafficLogListExpandedContentState> {
    /**
     * Select the log passed from the parent component.
     * If defaults, an empty object is guaranteed to be returned.
     */
    public override readonly log$ = this.select<TTrafficLog>(
        ({ applicationLog, connectionLog }) => {
            return !isObjectEmpty(applicationLog) ? applicationLog : connectionLog;
        },
    );

    public readonly applicationLog$ = this.select(
        ({ applicationLog }) => applicationLog,
    );

    public readonly connectionLog$ = this.select(
        ({ connectionLog }) => connectionLog,
    );

    /**
     * Indicate whether the log has Avi defined filters.
     */
    public readonly isSignificantLog$ = this.select(this.log$, ({ adf }) => adf);

    // ******************************** Generic Log Selectors ************************************

    public readonly clientRtt$ = this.select(this.log$, ({ client_rtt }) => client_rtt);

    public readonly serverRtt$ = this.select(this.log$, ({ server_rtt }) => server_rtt);

    public readonly totalTime$ = this.select(this.log$, ({ total_time }) => (
        total_time ? Number(total_time) : total_time
    ));

    public readonly significantLog$ = this.select(
        this.log$, ({ significant_log: significanceLog }) => significanceLog,
    );

    public readonly pool$ = this.select(this.log$, ({ pool }) => pool);

    public readonly poolName$ = this.select(this.log$, ({ pool_name }) => pool_name);

    public readonly clientIps$ = this.select(
        this.log$, ({ client_ip6, client_ip }) => {
            return {
                client_ip,
                client_ip6,
            };
        },
    );

    public readonly clientSrcPort$ = this.select(
        this.log$,
        ({ client_src_port }) => client_src_port,
    );

    public readonly clientDestPort$ = this.select(
        this.log$,
        ({ client_dest_port }) => client_dest_port,
    );

    public readonly vsIps$ = this.select(
        this.log$, ({ vs_ip, vs_ip6 }) => {
            return {
                vs_ip,
                vs_ip6,
            };
        },
    );

    public readonly serverName$ = this.select(
        this.log$,
        ({ server_name }) => server_name,
    );

    public readonly serverIps$ = this.select(
        this.log$, ({ server_ip, server_ip6 }) => {
            return {
                server_ip,
                server_ip6,
            };
        },
    );

    public readonly serverSrcPort$ = this.select(
        this.log$, ({ server_src_port }) => server_src_port,
    );

    public readonly serverDestPort$ = this.select(
        this.log$, ({ server_dest_port }) => server_dest_port,
    );

    public readonly serverConnectionSrcIps$ = this.select(
        this.log$,
        ({ server_conn_src_ip, server_conn_src_ip6 }) => {
            return {
                server_conn_src_ip,
                server_conn_src_ip6,
            };
        },
    );

    public readonly serviceEngine$ = this.select(
        this.log$, ({ service_engine }) => service_engine,
    );

    public readonly vcpuId$ = this.select(this.log$, ({ vcpu_id }) => vcpu_id);

    // ****************************** Application Log Selectors **********************************

    public readonly responseCode$ = this.select(
        this.applicationLog$,
        ({ response_code }) => response_code,
    );

    public readonly serverResponseCode$ = this.select(
        this.applicationLog$,
        ({ server_response_code }) => server_response_code,
    );

    public readonly appResponseTime$ = this.select(
        this.applicationLog$,
        ({ app_response_time }) => (
            app_response_time ? Number(app_response_time) : app_response_time
        ),
    );

    public readonly dataTransferTime$ = this.select(
        this.applicationLog$,
        ({ data_transfer_time }) => (
            data_transfer_time ? Number(data_transfer_time) : data_transfer_time
        ),
    );

    public readonly sourceIps$ = this.select(
        this.applicationLog$,
        ({ source_ip, source_ip6 }) => {
            return {
                source_ip,
                source_ip6,
            };
        },
    );

    public readonly ssl$ = this.select(
        this.applicationLog$,
        ({
            ssl_version,
            sni_hostname,
            ocsp_status_resp_sent,
            ssl_cipher,
            client_fingerprints,
            client_cipher_list,
        }) => {
            const ssl = {
                ssl_version,
                sni_hostname,
                ocsp_status_resp_sent,
                ssl_cipher,
                client_fingerprints,
                client_cipher_list,
            };

            if (isObjectEmpty(ssl)) {
                return null;
            }

            return ssl;
        },
    );

    public readonly wafLog$ = this.select(
        this.applicationLog$,
        ({ waf_log: wafLog }) => wafLog,
    );

    public readonly botManagementLog$ = this.select(
        this.applicationLog$,
        ({ bot_management_log: botManagementLog }) => botManagementLog,
    );

    public readonly datascriptInfo$ = this.select(
        this.applicationLog$,
        ({ datascript_error_trace, datascript_log }) => {
            const datascriptInfo = {
                datascript_error_trace,
                datascript_log,
            };

            if (isObjectEmpty(datascriptInfo)) {
                return null;
            }

            return datascriptInfo;
        },
    );

    public readonly icapLog$ = this.select(
        this.applicationLog$,
        ({ icap_log }) => icap_log,
    );

    // true as long as one of the authentication logs is present
    public readonly hasAuthenticationLogs$ = this.select(
        this.applicationLog$,
        ({
            saml_log,
            oauth_log,
            jwt_log,
            ntlm_log,
        }) => Boolean(saml_log || oauth_log || jwt_log || ntlm_log),
    );

    constructor(logsStore: LogsStore<ILogsStates>) {
        super(logsStore, initialState);
    }
}
